import { el } from '../ui.ts';
import { alleSafes, safeVon, type SafeAnzeige } from '../safes.ts';

/**
 * Bausteine für die Safe-Bedienung (Karsten, 16.10.2026): große Kacheln statt
 * Auswahlfeld, Farbe + Zeichen + Wort auf jedem Schild. Nie nur Farbe.
 */

/** Kleines Türschild mit Farbe und Buchstabe. */
export function tuerschild(safe: SafeAnzeige): HTMLElement {
  return el('span', { class: 'tuer', style: `--k:${safe.farbe}`, 'aria-hidden': 'true' }, safe.zeichen);
}

/** Schildchen am Dokument: zu welchem Safe es gehört. Nur, wenn es mehr als einen gibt. */
export function safeMarke(kundeId: string | null | undefined): HTMLElement | null {
  if (alleSafes().length < 2) return null;
  const safe = safeVon(kundeId);
  if (!safe) return null;
  return el('span', { class: 'marke safe', style: `--k:${safe.farbe}` }, safe.titel);
}

export function untertitel(safe: SafeAnzeige): string {
  if (safe.gesperrt) return 'Schlüssel kommt noch';
  if (safe.zugang === 'gast') return safe.umfang === 'faecher' ? 'nur bestimmte Fächer' : 'für mich geöffnet';
  if (safe.typ === 'privat') return 'nur ich';
  if (safe.typ === 'familie') return 'gemeinsam';
  return 'gemeinsam';
}

/**
 * Eine Frage, große Kacheln als Antwort. `gewaehlt` null = „Überall" (wenn
 * `mitUeberall`), sonst die Kennung eines Safes. Die Kacheln sind echte
 * Radioknöpfe: Tastatur, Vorleseprogramm und große Finger kommen zurecht.
 */
export function safeKacheln(options: {
  frage: string;
  safes: SafeAnzeige[];
  gewaehlt: string | null;
  mitUeberall?: boolean;
  /** Zusatzzeile je Kachel, z. B. „12 Dokumente". */
  zeile?: (safe: SafeAnzeige) => string | null;
  onWahl: (kundeId: string | null) => void;
  name?: string;
}): HTMLElement {
  const name = options.name ?? `safe-${Math.random().toString(36).slice(2, 8)}`;
  const kachel = (
    wert: string | null,
    farbe: string,
    zeichen: string,
    titel: string,
    unter: string | null,
    extra: Record<string, string | boolean> = {},
  ): HTMLElement => {
    const id = `${name}-${wert ?? 'alle'}`;
    const input = el('input', {
      type: 'radio',
      name,
      id,
      value: wert ?? '',
      ...(wert === options.gewaehlt ? { checked: true } : {}),
      ...(extra.disabled ? { disabled: true } : {}),
      onchange: () => options.onWahl(wert),
    });
    return el(
      'label',
      { class: `kachel${extra.breit ? ' breit' : ''}${extra.disabled ? ' gesperrt' : ''}`, for: id, style: `--k:${farbe}` },
      input,
      el('span', { class: 'tuer', 'aria-hidden': 'true' }, zeichen),
      el('span', { class: 'kachel-text' }, el('b', {}, titel), unter ? el('small', {}, unter) : null),
    );
  };

  const kacheln = options.safes.map((safe) =>
    kachel(safe.kundeId, safe.farbe, safe.zeichen, safe.titel, options.zeile?.(safe) ?? untertitel(safe), { disabled: safe.gesperrt }),
  );
  if (options.mitUeberall) {
    kacheln.push(kachel(null, 'var(--tb-navy-700)', '★', 'Überall', 'in allen Safes, die ich sehen darf', { breit: true }));
  }

  return el(
    'fieldset',
    { class: 'safe-kacheln' },
    el('legend', { class: 'frage' }, options.frage),
    el('div', { class: 'kachel-raster' }, ...kacheln),
  );
}
